/**
 * Экран одной подписки: подключение, срок, продление и её устройства.
 *
 * Ссылку подключения и список устройств даёт панель Remnawave.
 */
import { useNavigate, useParams } from 'react-router';

import { useSubscription } from '@/entities/subscription/queries';
import { ConnectBlock } from '@/features/connect/ConnectBlock';
import { DeviceList } from '@/features/devices/DeviceList';
import { describeState } from '@/entities/subscription/state';
import { daysWord, formatBytes, formatShortDate, formatTraffic } from '@/shared/lib/format';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Notice, Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { Card, GroupCaption } from '@/shared/ui/kit/Card';
import { Icon } from '@/shared/ui/kit/Icon';
import { Status } from '@/shared/ui/kit/Status';

import styles from './Subscriptions.module.css';

/** «3,2 ГБ из 25 ГБ»; расход неизвестен — только объём. */
function cdnUsage(used: number | null | undefined, limit: number): string {
  if (used === null || used === undefined) {
    return `до ${formatBytes(limit)}`;
  }
  return `${used === 0 ? '0 ГБ' : formatBytes(used)} из ${formatBytes(limit)}`;
}

export function SubscriptionPage() {
  const navigate = useNavigate();
  useBackButton(() => void navigate('/subscriptions'));
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useSubscription();

  const index = data?.items.findIndex((item) => item.id === id) ?? -1;
  const item = index >= 0 ? data?.items[index] : undefined;
  // Льготный период: срок прошёл, доступ ещё не отключён.
  const grace = item?.state === 'grace';

  if (!isLoading && !error && data && item === undefined) {
    return <Notice header="Подписка не найдена" description="Вернитесь к списку подписок." />;
  }

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {item ? (
        <>
          <h1 className={styles.head}>Подписка {index + 1}</h1>
          <Card padded>
            <div className={styles.between}>
              <Status tone={describeState(item.state).tone}>{describeState(item.state).label}</Status>
              <span className={`${styles.dim} ${styles.inline}`}>
                <Icon name="device" size={15} />
                {item.devices_used !== null && item.devices_used !== undefined
                  ? `${item.devices_used} из ${item.device_limit ?? '∞'} устр.`
                  : `до ${item.device_limit ?? '∞'} устр.`}
              </span>
            </div>

            <div className={styles.stack}>
              {/* Подключаться к закончившейся подписке не к чему. */}
              {item.has_access ? (
                <ConnectBlock url={item.subscription_url} onRefresh={() => void refetch()} />
              ) : null}

              <div className={styles.facts}>
                <div className={styles.fact}>
                  <span className={styles.dim}>
                    {item.has_access && !grace ? 'Действует до' : 'Закончилась'}
                  </span>
                  <span className={styles.value}>
                    {item.is_lifetime || !item.expires_at ? 'Бессрочно' : formatShortDate(item.expires_at)}
                    {grace ? (
                      <span className={styles.sub}>доступ ещё открыт</span>
                    ) : item.has_access && item.days_left !== null ? (
                      <span className={styles.sub}>
                        осталось {item.days_left} {daysWord(item.days_left)}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className={styles.fact}>
                  <span className={styles.dim}>Трафик</span>
                  <span className={styles.value}>
                    {formatTraffic(item.traffic_used_bytes, item.traffic_limit_bytes)}
                  </span>
                </div>
                {item.has_access && item.cdn_limit_bytes != null ? (
                  <div className={styles.fact}>
                    <span className={styles.dim}>Белый список</span>
                    <span className={styles.value}>
                      {cdnUsage(item.cdn_used_bytes, item.cdn_limit_bytes)}
                      <span className={styles.sub}>
                        {item.cdn_exhausted ? 'закончился — докупите или продлите' : 'на весь срок'}
                      </span>
                    </span>
                  </div>
                ) : null}
              </div>

              {item.has_access && item.cdn_limit_bytes != null ? (
                <Button
                  variant={item.cdn_exhausted ? 'primary' : 'quiet'}
                  block
                  onClick={() => void navigate('/packs')}
                >
                  Докупить гигабайты «Белого списка»
                </Button>
              ) : null}
              <Button
                variant={item.has_access && !grace ? 'quiet' : 'primary'}
                block
                onClick={() =>
                  // Закончившуюся тоже продлеваем на месте — с той же ссылкой.
                  void navigate(
                    item.has_access || item.state === 'expired' ? `/plans?extend=${item.id}` : '/plans',
                  )
                }
              >
                {item.has_access ? 'Продлить' : 'Возобновить'}
              </Button>
            </div>
          </Card>

          <GroupCaption>Устройства</GroupCaption>
          <DeviceList subscriptionId={item.id} active={item.has_access} />
        </>
      ) : null}
    </Screen>
  );
}
